import React, { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    FiShoppingCart,
    FiTrash2,
    FiShield,
    FiTruck,
    FiCheckCircle,
    FiArrowRight,
    FiShoppingBag,
    FiTag,
    FiPhoneCall,
} from 'react-icons/fi';
import { useShop } from '../context/ShopContext';
import { useDiscount } from '../context/DiscountContext';
import { getImageUrl } from '../utils/imageUrl';
import PlaceOrderModal from '../components/cart/PlaceOrderModal';
import SuccessModal from '../components/common/SuccessModal';
import {
    cashPriceFromMrp,
    getProductDiscount,
    totalCashForLine,
    totalMrpForLine,
    formatPrice,
} from '../utils/cashPricing';
import './CartPage.css';

const CartPage = () => {
    const { cart, updateCartQuantity, removeFromCart, clearCart } = useShop();
    const { effectiveDiscount } = useDiscount();
    const [placeOrderOpen, setPlaceOrderOpen] = useState(false);
    const [placedOrder, setPlacedOrder] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const cartForApi = useMemo(
        () =>
            cart.map((item) => {
                const mrp = Number(item.mrp ?? item.cash_price ?? 0);
                const itemDiscount = getProductDiscount(item, effectiveDiscount);
                const cashUnit = cashPriceFromMrp(mrp, itemDiscount);
                const qty = item.quantity || 1;
                return {
                    productId: String(item.id || item._id || ''),
                    product_id: String(item.id || item._id || ''),
                    name: item.name || 'Home Appliance',
                    brand: item.brand || 'Rahber Electronics',
                    sku: item.sku || '',
                    image: item.image || '',
                    quantity: qty,
                    unitPrice: cashUnit,
                    unit_price: cashUnit,
                    price: cashUnit,
                    totalPrice: cashUnit * qty,
                    total_price: cashUnit * qty,
                    mrp: mrp,
                    payment_plan: 'cash',
                    plan_details: 'Direct Cash Purchase',
                };
            }),
        [cart, effectiveDiscount]
    );

    const handleOrderSuccess = useCallback(
        (order) => {
            // Cart is ONLY cleared once backend confirms successful creation
            clearCart();
            setPlacedOrder(order);
        },
        [clearCart]
    );

    const decrementOrRemove = (item) => {
        if (item.quantity <= 1) removeFromCart(item.id);
        else updateCartQuantity(item.id, item.quantity - 1);
    };

    const { mrpSubtotal, cashSubtotal, totalItemsCount } = useMemo(() => {
        let m = 0;
        let c = 0;
        let count = 0;
        for (const item of cart) {
            const mrp = item.mrp ?? item.cash_price ?? 0;
            const qty = item.quantity || 0;
            const itemDiscount = getProductDiscount(item, effectiveDiscount);
            m += totalMrpForLine(mrp, qty);
            c += totalCashForLine(mrp, qty, itemDiscount);
            count += qty;
        }
        return { mrpSubtotal: m, cashSubtotal: c, totalItemsCount: count };
    }, [cart, effectiveDiscount]);

    const cashDiscountAmount = Math.max(0, mrpSubtotal - cashSubtotal);

    return (
        <div className="cart-page">
            {/* Showroom Header */}
            <header className="cart-hero-header">
                <div className="container">
                    <div className="cart-hero-content">
                        <div className="cart-hero-badge">
                            <FiShoppingBag /> Official Showroom Cart
                        </div>
                        <h1 className="cart-hero-title">
                            Shopping Cart
                            {totalItemsCount > 0 && (
                                <span className="cart-count-pill">
                                    {totalItemsCount} {totalItemsCount === 1 ? 'item' : 'items'}
                                </span>
                            )}
                        </h1>
                        <p className="cart-hero-subtitle">
                            Every cash purchase receives an automatic <strong>Instant Cash Discount</strong> off official manufacturer list MRP.
                        </p>
                    </div>
                </div>
            </header>

            <main className="container cart-page-inner">
                {cart.length === 0 ? (
                    <div className="cart-empty-luxury">
                        <div className="cart-empty-icon-wrap">
                            <FiShoppingCart className="cart-empty-icon" />
                        </div>
                        <h2>Your Cart is Currently Empty</h2>
                        <p>
                            Explore our premium showroom collection of refrigerators, air conditioners, LED TVs,
                            and home electronics with verified cash savings.
                        </p>
                        <div className="cart-empty-actions">
                            <Link to="/products" className="cart-btn-primary">
                                <FiShoppingBag /> Browse All Products
                            </Link>
                            <Link to="/wishlist" className="cart-btn-secondary">
                                View Saved Wishlist
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="cart-layout-grid">
                        {/* Cart Items List */}
                        <section className="cart-items-section" aria-label="Cart Items">
                            <div className="cart-items-header-bar">
                                <span className="cart-items-col-title">Showroom Products ({cart.length})</span>
                                <button
                                    type="button"
                                    className="cart-clear-all-btn"
                                    onClick={() => {
                                        if (window.confirm('Are you sure you want to clear your cart?')) {
                                            clearCart();
                                        }
                                    }}
                                >
                                    <FiTrash2 /> Clear Cart
                                </button>
                            </div>

                            <div className="cart-items-list">
                                {cart.map((item, index) => {
                                    const img = getImageUrl(item.image);
                                    const mrpUnit = Number(item.mrp ?? item.cash_price ?? 0) || 0;
                                    const itemDiscount = getProductDiscount(item, effectiveDiscount);
                                    const cashUnit = cashPriceFromMrp(mrpUnit, itemDiscount);
                                    const qty = item.quantity || 0;
                                    const lineMrpTotal = totalMrpForLine(mrpUnit, qty);
                                    const lineCashTotal = cashUnit * qty;
                                    const lineSaving = Math.max(0, lineMrpTotal - lineCashTotal);

                                    return (
                                        <article
                                            key={item.id}
                                            className="cart-item-card"
                                            style={{ animationDelay: `${index * 60}ms` }}
                                        >
                                            {/* Thumbnail with savings badge */}
                                            <div className="cart-item-image-wrapper">
                                                <Link to={`/products/${item.id}`} className="cart-item-image-link">
                                                    {img ? (
                                                        <img
                                                            src={img}
                                                            alt={item.name}
                                                            loading="lazy"
                                                            className="cart-item-img"
                                                        />
                                                    ) : (
                                                        <div className="cart-item-placeholder">No Image</div>
                                                    )}
                                                </Link>
                                                {itemDiscount > 0 && (
                                                    <span className="cart-item-discount-tag">
                                                        -{itemDiscount}%
                                                    </span>
                                                )}
                                            </div>

                                            {/* Details & Specs */}
                                            <div className="cart-item-details">
                                                <div className="cart-item-top">
                                                    <span className="cart-item-brand">
                                                        {item.brand || 'Rahber Electronics'}
                                                    </span>
                                                    {item.sku && (
                                                        <span className="cart-item-sku">
                                                            SKU: <code>{item.sku}</code>
                                                        </span>
                                                    )}
                                                </div>

                                                <Link
                                                    to={`/products/${item.id}`}
                                                    className="cart-item-name"
                                                    title={item.name}
                                                >
                                                    {item.name}
                                                </Link>

                                                {/* Single Unit Price block with strike MRP */}
                                                <div className="cart-item-pricing-row">
                                                    <div className="cart-price-group">
                                                        <span className="cart-mrp-strike">
                                                            MRP: {formatPrice(mrpUnit)}
                                                        </span>
                                                        <span className="cart-cash-unit">
                                                            <strong>{formatPrice(cashUnit)}</strong>
                                                            <small> / unit (cash)</small>
                                                        </span>
                                                    </div>
                                                    {lineSaving > 0 && (
                                                        <span className="cart-saving-pill">
                                                            Save {formatPrice(lineSaving)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Quantity and Line Total */}
                                            <div className="cart-item-actions-cluster">
                                                <div className="cart-qty-wrapper">
                                                    <label className="cart-qty-label">Quantity</label>
                                                    <div className="cart-qty-stepper">
                                                        <button
                                                            type="button"
                                                            className="cart-qty-btn cart-qty-btn--minus"
                                                            onClick={() => decrementOrRemove(item)}
                                                            aria-label="Decrease quantity"
                                                        >
                                                            −
                                                        </button>
                                                        <span className="cart-qty-value">{item.quantity}</span>
                                                        <button
                                                            type="button"
                                                            className="cart-qty-btn cart-qty-btn--plus"
                                                            onClick={() =>
                                                                updateCartQuantity(item.id, item.quantity + 1)
                                                            }
                                                            aria-label="Increase quantity"
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="cart-line-total-wrapper">
                                                    <span className="cart-line-total-label">Line Total</span>
                                                    <strong className="cart-line-total-amount">
                                                        {formatPrice(lineCashTotal)}
                                                    </strong>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="cart-item-remove-btn"
                                                    onClick={() => removeFromCart(item.id)}
                                                    title="Remove item"
                                                    aria-label={`Remove ${item.name}`}
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>

                            {/* Trust badges footer */}
                            <div className="cart-trust-footer-strip">
                                <div className="cart-trust-pill">
                                    <FiShield className="cart-trust-icon" />
                                    <span>100% Genuine Sealed Box</span>
                                </div>
                                <div className="cart-trust-pill">
                                    <FiTruck className="cart-trust-icon" />
                                    <span>Insured Lahore Delivery</span>
                                </div>
                                <div className="cart-trust-pill">
                                    <FiCheckCircle className="cart-trust-icon" />
                                    <span>Brand Warranty Card Included</span>
                                </div>
                            </div>
                        </section>

                        {/* Order Summary Aside */}
                        <aside className="cart-summary-card">
                            <div className="cart-summary-card-inner">
                                <div className="cart-summary-header">
                                    <h2 className="cart-summary-title">Order Summary</h2>
                                    <span className="cart-summary-badge">Cash Checkout</span>
                                </div>

                                {cashDiscountAmount > 0 && (
                                    <div className="cart-summary-discount-banner">
                                        <FiTag className="cart-summary-tag-icon" />
                                        <div>
                                            <strong>Instant Cash Discount Applied</strong>
                                            <p>Guaranteed genuine showroom cash pricing</p>
                                        </div>
                                    </div>
                                )}

                                <div className="cart-summary-rows">
                                    <div className="cart-summary-row">
                                        <span className="cart-summary-row-label">List Price (MRP)</span>
                                        <span className="cart-summary-row-val cart-summary-row-val--mrp">
                                            {formatPrice(mrpSubtotal)}
                                        </span>
                                    </div>

                                    {cashDiscountAmount > 0 && (
                                        <div className="cart-summary-row cart-summary-row--savings">
                                            <span className="cart-summary-row-label">
                                                Instant Cash Savings
                                            </span>
                                            <span className="cart-summary-row-val cart-summary-row-val--discount">
                                                − {formatPrice(cashDiscountAmount)}
                                            </span>
                                        </div>
                                    )}

                                    <div className="cart-summary-row">
                                        <span className="cart-summary-row-label">Lahore Showroom Delivery</span>
                                        <span className="cart-summary-row-val cart-summary-row-val--free">
                                            FREE
                                        </span>
                                    </div>

                                    <div className="cart-summary-divider" />

                                    <div className="cart-summary-total-row">
                                        <div>
                                            <span className="cart-total-title">Total Cash Payable</span>
                                            <small className="cart-total-subtitle">Bank transfer or showroom pickup</small>
                                        </div>
                                        <div className="cart-total-price-wrap">
                                            <strong className="cart-total-price">
                                                {formatPrice(cashSubtotal)}
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="cart-checkout-proceed-btn"
                                    onClick={() => setPlaceOrderOpen(true)}
                                >
                                    <span>Proceed to Place Order</span>
                                    <FiArrowRight />
                                </button>

                                <div className="cart-summary-perks">
                                    <div className="cart-perk-item">
                                        <FiPhoneCall className="perk-icon" />
                                        <span>Dispatch manager calls before delivery</span>
                                    </div>
                                    <div className="cart-perk-item">
                                        <FiShield className="perk-icon" />
                                        <span>Pay after physical inspection at doorstep</span>
                                    </div>
                                </div>

                                <div className="cart-summary-footer-links">
                                    <Link to="/products" className="cart-back-shopping-link">
                                        ← Continue Shopping
                                    </Link>
                                </div>
                            </div>
                        </aside>
                    </div>
                )}
            </main>

            {/* Place Order Modal */}
            <PlaceOrderModal
                open={placeOrderOpen}
                onClose={() => {
                    setPlaceOrderOpen(false);
                    if (placedOrder) {
                        setShowSuccessModal(true);
                    }
                }}
                cartForApi={cartForApi}
                cartItems={cart}
                onSuccess={handleOrderSuccess}
                cartSummary={{
                    mrpSubtotal,
                    cashSubtotal,
                    cashDiscountAmount,
                    totalItemsCount,
                }}
            />

            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => {
                    setShowSuccessModal(false);
                    setPlacedOrder(null);
                }}
                title="Cash Order Placed Successfully!"
                message="Thank you for ordering with Rahber Corporation. Our showroom dispatch manager will call you shortly to confirm your order and delivery address."
                referenceId={placedOrder?.order_number || placedOrder?.id}
                phone={placedOrder?.customer_phone}
                productName={
                    placedOrder?.items?.[0]?.name
                        ? `${placedOrder.items[0].name}${placedOrder.items.length > 1 ? ` (+${placedOrder.items.length - 1} more items)` : ''}`
                        : undefined
                }
                orderTotal={placedOrder?.total_amount ? formatPrice(placedOrder.total_amount) : undefined}
                isOrder={true}
                order={placedOrder}
            />
        </div>
    );
};

export default CartPage;
